import { FC, useEffect, useMemo } from "react";

import {
  AttachmentI,
  EmailTemplateI,
  GetEmailGenerationInfoResponseI,
} from "@/api/routes/email";

import { useAccountDetailsContext } from "@/modules/pipeline/account-details/context";

import { useEmailTemplatesContext } from "@/modules/email-templates-sidebar/context";
import { EmailForm } from "./email-form";

import {
  EmailDraftI,
  EmailSequenceItemI,
} from "@/components/modules/email-templates-sidebar/interfaces";
import {
  EMAIL_DRAFT_FIELDS,
  EMAIL_TEMPLATES_TABS,
} from "@/modules/email-templates-sidebar/constants";
import { BadgeI } from "shared/ui/user-input/badge-input";
import { EmailFormActionsSection } from "./email-form/actions-section";
import { useEmailSender } from "@/components/modules/email-templates-sidebar/hooks";
import { dispatchCustomEvent } from "@/helpers/events";
import { CUSTOM_EVENTS } from "@/constants/custom-events";
import { useAIGenerationDraftSync } from "./use-ai-generation-draft-sync";

interface EmailTemplatePropsI {
  template: EmailTemplateI;
  config: GetEmailGenerationInfoResponseI;
  campaignAttachments?: AttachmentI[];
}

const Content: FC<EmailTemplatePropsI> = ({
  template,
  config,
  campaignAttachments,
}) => {
  const {
    drafts,
    activeEmailSequenceItemId,
    isAIGenerationComplete,
    isAIGenerationStarted,
    isEmailSent,
    emailData,
    handleComposeWithAI,
    setDrafts,
    setEmailData,
    setIsEmailSent,
    setIsAIGenerationComplete,
    setIsAIGenerationStarted,
    getDraft,
    setDraft,

    setActiveTab,
  } = useEmailTemplatesContext();

  const {
    // setFocusedContact,
    accountHistoryData: { reloadData: reloadAccountHistory },
  } = useAccountDetailsContext();

  const draftId = template?.id;

  const onEmailSuccess = () => {
    setIsEmailSent?.(true);
    reloadAccountHistory();

    setDrafts({});

    /**
     * The generation belongs to the email that just went out. Leaving it in
     * place would let it seed the next compose for a different prospect.
     */
    setEmailData(undefined);

    setActiveTab(EMAIL_TEMPLATES_TABS.HISTORY);
    setIsEmailSent(false);

    /**
     * If currently on the call do nothing
     */

    /**
     * If call has ended and we got here
     * though email forma after dispositioning
     * we want to cleanup the call and remove email section
     * from dialer toolkit
     */

    dispatchCustomEvent({
      eventType: CUSTOM_EVENTS.ACCOUNT.EMAIL_TEMPLATES.ON_EMAIL_SEND,
    });

    // setFocusedContact(undefined);
    // onClose();
  };

  const { handler: handleSendEmail, isPending: isSendEmailPending } =
    useEmailSender(onEmailSuccess);

  /**
   * Update draft primary email
   * @param field - field to update (subject or body)
   * @param value - value to update
   */
  const updatePrimaryEmail = (field: keyof EmailDraftI, value?: any) => {
    if (draftId) {
      const draft = getDraft(draftId);

      setDraft(draftId, {
        ...draft,
        [field]: value,
      });
    }
  };

  /**
   * Update draft email in sequence
   * @param field - field to update (subject or body or attachments)
   * @param text - text to update
   */
  const updateEmailInSequence = (
    field: keyof EmailSequenceItemI,
    text?: string | AttachmentI[]
  ) => {
    if (draftId && activeEmailSequenceItemId) {
      const draft = getDraft(draftId);

      setDraft(draftId, {
        ...draft,
        emails: draft?.emails?.map((email) =>
          email.email_id === activeEmailSequenceItemId
            ? { ...email, [field]: text }
            : email
        ),
      });
    }
  };

  /**
   * Update email draft field
   * @param field - field to update (subject or body)
   * @param text - text to update
   */
  const updateEmailDraftField = (
    field:
      | typeof EMAIL_DRAFT_FIELDS.SUBJECT
      | typeof EMAIL_DRAFT_FIELDS.BODY
      | typeof EMAIL_DRAFT_FIELDS.ATTACHMENTS,
    text?: string | AttachmentI[]
  ) => {
    if (activeEmailSequenceItemId) {
      updateEmailInSequence(field, text);
      return;
    }

    updatePrimaryEmail(field, text);
  };

  /**
   * Event handlers ------------------------------------------------------------
   */

  const handleOnChangeSubject = (subject?: string) => {
    updateEmailDraftField(EMAIL_DRAFT_FIELDS.SUBJECT, subject);
  };

  const handleOnChangeBody = (body?: string) => {
    updateEmailDraftField(EMAIL_DRAFT_FIELDS.BODY, body);
  };

  const handleOnChangeAttachments = (attachments: AttachmentI[]) => {
    updateEmailDraftField(EMAIL_DRAFT_FIELDS.ATTACHMENTS, attachments);
  };

  const handleOnChangeRecipientsTo = (to: BadgeI[]) => {
    updatePrimaryEmail(EMAIL_DRAFT_FIELDS.TO, to);
  };

  const handleOnChangeRecipientsCc = (cc: BadgeI[]) => {
    updatePrimaryEmail(EMAIL_DRAFT_FIELDS.CC, cc);
  };

  const handleOnChangeRecipientsBcc = (bcc: BadgeI[]) => {
    updatePrimaryEmail(EMAIL_DRAFT_FIELDS.BCC, bcc);
  };

  /**
   * Memoized valirables ------------------------------------------------------------
   */

  /**
   * NOTE
   * We need to check if the email is a primary email or the first one in the sequence
   */
  const isPrimaryEmail = useMemo(() => {
    const draft = getDraft(draftId as string);

    return (
      !activeEmailSequenceItemId ||
      draft?.emails?.[0]?.email_id === activeEmailSequenceItemId
    );
  }, [activeEmailSequenceItemId]);

  const draft = useMemo(() => {
    return getDraft(draftId as string);
  }, [drafts, draftId]);

  const subject = useMemo(() => {
    if (!draftId) return "";

    const draft = getDraft(draftId);

    if (draft && activeEmailSequenceItemId) {
      const primarySubject = draft?.emails?.[0]?.subject || "";

      const emailIndex =
        draft?.emails?.findIndex(
          (email) => email.email_id === activeEmailSequenceItemId
        ) || 0;

      return emailIndex > 0 ? `Re: ${primarySubject}` : primarySubject;
    }

    return draft?.subject || "";
  }, [activeEmailSequenceItemId, draftId, drafts, isAIGenerationComplete]);

  const body = useMemo(() => {
    if (!draftId) return "";

    const draft = getDraft(draftId);

    if (draft && activeEmailSequenceItemId) {
      return (
        getDraft(draftId)?.emails?.find(
          (email) => email.email_id === activeEmailSequenceItemId
        )?.body || ""
      );
    }

    return draft?.body || "";
  }, [activeEmailSequenceItemId, draftId, drafts, isAIGenerationComplete]);

  const attachments = useMemo(() => {
    if (!draftId) return [];

    const draft = getDraft(draftId);

    if (draft?.emails?.length) {
      return (
        draft?.emails?.find(
          (email) => email.email_id === activeEmailSequenceItemId
        )?.attachments || []
      );
    }

    return draft?.attachments || [];
  }, [draftId, activeEmailSequenceItemId]);

  /**
   * Email Generation Side Effect handlers ------------------------------------------------------------
   */

  useAIGenerationDraftSync({
    subject: emailData?.subject,
    body: emailData?.body,
    isGenerating: isAIGenerationStarted,
    onApplySubject: (subject) =>
      updateEmailDraftField(EMAIL_DRAFT_FIELDS.SUBJECT, subject),
    onApplyBody: (body) => updateEmailDraftField(EMAIL_DRAFT_FIELDS.BODY, body),
  });

  useEffect(() => {
    // reset/initialize content for selected template
    setIsEmailSent(false);
    setIsAIGenerationComplete(false);
    setIsAIGenerationStarted(false);
  }, [template.id]);

  return (
    <div className="relative animate-fadein">
      <EmailForm
        //  key is used to force entire component to re-render when template changes so that all form fields can be reset
        key={`${draftId}-${activeEmailSequenceItemId}`}
        template={template}
        senderEmail={config?.from_email}
        attachmentOptions={campaignAttachments}
        initialSubject={subject}
        initialBody={body}
        initialAttachments={attachments}
        initialRecipientsTo={draft?.to as BadgeI[]}
        initialRecipientsCc={draft?.cc as BadgeI[]}
        initialRecipientsBcc={draft?.bcc as BadgeI[]}
        onComposeWithAI={handleComposeWithAI}
        onSend={handleSendEmail}
        isSendingEmail={isSendEmailPending}
        isSendEmailSuccess={isEmailSent}
        isAIGenerationComplete={isAIGenerationComplete}
        isAIGenerating={isAIGenerationStarted}
        onChangeSubject={handleOnChangeSubject}
        onChangeBody={handleOnChangeBody}
        onChangeAttachments={handleOnChangeAttachments}
        onChangeRecipientsTo={handleOnChangeRecipientsTo}
        onChangeRecipientsCc={handleOnChangeRecipientsCc}
        onChangeRecipientsBcc={handleOnChangeRecipientsBcc}
        isDisabledSubjectSection={!isPrimaryEmail}
        hideRecipientsSection={!isPrimaryEmail}
        hideAttachmentSection={false}
        hideTitle={true}
        hideAiGenerateButton={true}
        hideIntro={true}
        actionsSection={({ submitForm }) => (
          <EmailFormActionsSection
            className="fixed bottom-0 left-0 right-0"
            isSendingEmail={isSendEmailPending}
            isSendEmailSuccess={isEmailSent}
            isAIGenerating={isAIGenerationStarted}
            onSubmit={submitForm}
          />
        )}
      />
    </div>
  );
};

export const EmailFormSection = () => {
  const { activeTemplate, emailGenerationConfig, campaignAttachmentsData } =
    useEmailTemplatesContext();

  return activeTemplate && emailGenerationConfig ? (
    <Content
      template={activeTemplate}
      config={emailGenerationConfig}
      campaignAttachments={campaignAttachmentsData?.attachments}
    />
  ) : null;
};
